import DOMPurify from 'dompurify';

/**
 * Sanitization utilities for preventing XSS and injection attacks
 * 
 * @module sanitizer
 * @description
 * Provides a comprehensive set of functions for sanitizing user inputs,
 * validating data, and preventing common security vulnerabilities.
 * All functions use DOMPurify for robust XSS protection.
 */

/**
 * Sanitize HTML content allowing only safe tags
 * 
 * @param html - Raw HTML string to sanitize
 * @returns Sanitized HTML with only allowed tags and no dangerous attributes
 * 
 * @example
 * ```ts
 * const clean = sanitizeHtml('<p>Safe</p><script>alert("XSS")</script>');
 * // Returns: '<p>Safe</p>'
 * ```
 */
export const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'b', 'i'],
    ALLOWED_ATTR: [],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'meta', 'link'],
    FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'style', 'href', 'src']
  });
};

/**
 * Sanitize text content by stripping all HTML tags
 * 
 * @param text - Raw text that may contain HTML
 * @returns Plain text with all HTML removed
 * 
 * @example
 * ```ts
 * const clean = sanitizeText('Hello <b>world</b>');
 * // Returns: 'Hello world'
 * ```
 */
export const sanitizeText = (text: string): string => {
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

/**
 * Validate and sanitize URLs to prevent javascript: and data: URIs
 * 
 * @param url - URL string to validate
 * @returns Sanitized URL if valid, empty string if invalid
 * 
 * @example
 * ```ts
 * sanitizeUrl('https://example.com'); // 'https://example.com'
 * sanitizeUrl('javascript:alert(1)'); // ''
 * ```
 */
export const sanitizeUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return '';
    }
    return url;
  } catch {
    return '';
  }
};

/**
 * Validate and sanitize text input with length limits and suspicious pattern detection
 * 
 * @param input - Text input to validate
 * @param maxLength - Maximum allowed length (default: 1000)
 * @returns Object with validation result, sanitized text, and any errors
 * 
 * @example
 * ```ts
 * const result = validateInput('Hello world', 100);
 * if (result.isValid) {
 *   console.log(result.sanitized);
 * } else {
 *   console.error(result.errors);
 * }
 * ```
 */
export const validateInput = (input: string, maxLength: number = 1000): { isValid: boolean; sanitized: string; errors: string[] } => {
  const errors: string[] = [];
  
  if (!input || input.trim().length === 0) {
    errors.push('Campo obrigatório');
    return { isValid: false, sanitized: '', errors };
  }
  
  if (input.length > maxLength) {
    errors.push(`Máximo de ${maxLength} caracteres permitidos`);
  }
  
  const sanitized = sanitizeText(input.trim());
  
  // Check for suspicious patterns
  const suspiciousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /data:text\/html/i,
    /vbscript:/i,
    /expression\s*\(/i
  ];
  
  if (suspiciousPatterns.some(pattern => pattern.test(input))) {
    errors.push('Conteúdo não permitido detectado');
  }
  
  return {
    isValid: errors.length === 0,
    sanitized,
    errors
  };
};

/**
 * Generate a cryptographically secure CSRF token
 * 
 * @returns 64-character hexadecimal CSRF token
 * 
 * @example
 * ```ts
 * const token = generateCSRFToken();
 * sessionStorage.setItem('csrf_token', token);
 * ```
 */
export const generateCSRFToken = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Validate CSRF token against stored token
 * 
 * @param token - Token from request
 * @param storedToken - Token stored in session
 * @returns true if tokens match, false otherwise
 */
export const validateCSRFToken = (token: string, storedToken: string): boolean => {
  if (!token || !storedToken) return false;
  return token === storedToken;
};

/**
 * Sanitize generic text input by trimming and removing HTML
 * 
 * @param input - Raw input string
 * @returns Sanitized text without HTML tags
 */
export const sanitizeInput = (input: string): string => {
  return DOMPurify.sanitize(input.trim(), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

/**
 * Sanitize and validate email addresses
 * 
 * @param email - Email address to sanitize
 * @returns Sanitized lowercase email if valid, empty string if invalid
 * 
 * @example
 * ```ts
 * sanitizeEmail('User@Example.com'); // 'user@example.com'
 * sanitizeEmail('invalid-email'); // ''
 * ```
 */
export const sanitizeEmail = (email: string): string => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const sanitized = sanitizeInput(email.toLowerCase());
  return emailRegex.test(sanitized) ? sanitized : '';
};

/**
 * Validate that a field is not empty and sanitize it
 * 
 * @param value - Input value to validate
 * @param fieldName - Name of the field for error messages
 * @returns Sanitized value
 * @throws Error if field is empty
 */
export const validateRequired = (value: string, fieldName: string): string => {
  const sanitized = sanitizeInput(value);
  if (!sanitized || sanitized.length === 0) {
    throw new Error(`${fieldName} é obrigatório`);
  }
  return sanitized;
};

/**
 * Validate and sanitize email with error throwing
 * 
 * @param email - Email address to validate
 * @returns Sanitized email
 * @throws Error if email is invalid
 */
export const validateEmail = (email: string): string => {
  const sanitized = sanitizeEmail(email);
  if (!sanitized) {
    throw new Error('Email inválido');
  }
  return sanitized;
};